/**
 * Orders page QA fixtures — flip ORDERS_PAGE_TEST_MODE or set localStorage
 * `stjeans-orders-page-test-mode` to `1` to use hardcoded data instead of the API.
 */

/** Set true to enable test mode without localStorage. */
export const ORDERS_PAGE_TEST_MODE = false

const LOCAL_STORAGE_KEY = "stjeans-orders-page-test-mode"

/**
 * @param {boolean | undefined} explicitOverride
 * @returns {boolean}
 */
export function isOrdersPageTestModeEnabled(explicitOverride = undefined) {
    if (typeof explicitOverride === "boolean") {
        return explicitOverride
    }
    if (ORDERS_PAGE_TEST_MODE) {
        return true
    }
    try {
        return typeof localStorage !== "undefined" &&
            localStorage.getItem(LOCAL_STORAGE_KEY) === "1"
    } catch {
        return false
    }
}

/** @param {boolean} enabled */
export function setOrdersPageTestModeEnabled(enabled) {
    try {
        if (enabled) {
            localStorage.setItem(LOCAL_STORAGE_KEY, "1")
        } else {
            localStorage.removeItem(LOCAL_STORAGE_KEY)
        }
    } catch {
        // ignore
    }
}

/** Locations referenced by ORDERS_PAGE_TEST_ORDERS (orders-page fields only). */
export const ORDERS_PAGE_TEST_LOCATIONS = [
    {
        id: 101,
        name: "Van",
        short_code: "VAN",
        is_lodge: false,
    },
    {
        id: 102,
        name: "Haida Lodge",
        lodge_code: "HG",
        is_lodge: true,
    },
    {
        id: 103,
        name: "St. Jean's Port Hardy Processing Facility",
        short_code: "PHPRC",
        is_lodge: false,
        allow_pick_up: true,
        pick_up_name: "Port Hardy Customer Pickup Counter — Rear Loading Dock",
    },
    {
        id: 104,
        name: "Queen Charlotte Lodge",
        lodge_code: "QCL",
        is_lodge: true,
        allow_pick_up: true,
        pick_up_name: "QCL",
    },
]

/**
 * Raw order records (API shape). Mapped by OrdersPage.mapOrderForList for desktop + mobile.
 * @type {Array<{
 *   id: number,
 *   master: string,
 *   status: string,
 *   location_id?: number,
 *   lodge_id?: number,
 *   pick_up_location_id?: number | null,
 *   ship_to: { first_name?: string, last_name?: string },
 *   bill_to?: { first_name?: string, last_name?: string },
 *   fish_tag?: string,
 *   trip_code?: string,
 *   st_jeans_num?: string,
 *   ordered_at: string,
 * }>}
 */
export const ORDERS_PAGE_TEST_ORDERS = [
    {
        id: 9001,
        master: "M-240001",
        status: "new",
        location_id: 101,
        ship_to: { first_name: "Ann", last_name: "Lee" },
        fish_tag: "3, 7",
        trip_code: "T1",
        st_jeans_num: "SJ-1001",
        ordered_at: "2026-03-15T14:22:00Z",
    },
    {
        id: 9002,
        master: "M-240002-EXTREMELY-LONG-MASTER-NUMBER-FOR-TRUNCATION-QA",
        status: "partially_boxed_up",
        lodge_id: 102,
        ship_to: {
            first_name: "Bartholomew-Constantine",
            last_name: "Worthington-Fitzgerald-Montgomery",
        },
        fish_tag: "12, 34, 56, 78, 90, 11, 22",
        trip_code: "TRIP-2026-HG-ALPHA-EXPEDITION",
        st_jeans_num: "SJ-2026-000002-PORT-HARDY-EXPORT",
        ordered_at: "2026-03-14T09:05:00Z",
    },
    {
        id: 9003,
        master: "M-240003-LONG-MASTER",
        status: "confirmed",
        location_id: 101,
        ship_to: { first_name: "Bo", last_name: "Wu" },
        fish_tag: "5",
        trip_code: "T3",
        st_jeans_num: "SJ-1003",
        ordered_at: "2026-03-13T16:40:00Z",
    },
    {
        id: 9004,
        master: "M-240004",
        status: "received",
        location_id: 103,
        pick_up_location_id: 103,
        ship_to: { first_name: "Cara", last_name: "Nguyen" },
        fish_tag: "1, 2, 3, 4",
        trip_code: "PU-LONG",
        st_jeans_num: "SJ-1004",
        ordered_at: "2026-03-12T11:18:00Z",
    },
    {
        id: 9005,
        master: "M-240005",
        status: "shipped",
        lodge_id: 104,
        pick_up_location_id: 104,
        ship_to: { first_name: "Dee", last_name: "Park" },
        fish_tag: "101, 202, 303, 404, 505, 606",
        trip_code: "QCL-26",
        ordered_at: "2026-03-11T08:00:00Z",
    },
    {
        id: 9006,
        master: "M-240006",
        status: "weighed_in",
        location_id: 103,
        ship_to: {
            first_name: "Eleanor",
            last_name: "Kowalski-Brennan-O'Sullivan",
        },
        fish_tag: "999",
        trip_code: "MID",
        st_jeans_num: "SJ-1006-MID-LENGTH-REFERENCE",
        ordered_at: "2026-03-10T19:33:00Z",
    },
    {
        id: 9007,
        master: "M-240007",
        status: "boxed_up",
        lodge_id: 102,
        ship_to: {
            first_name: "Francisco",
            last_name: "de la Vega y Mendoza",
        },
        fish_tag: "8, 9",
        trip_code: "HG-BETA",
        st_jeans_num: "SJ-1007",
        ordered_at: "2026-03-09T07:12:00Z",
    },
    {
        id: 9008,
        master: "M-240008",
        status: "invoiced",
        location_id: 101,
        ship_to: {
            first_name: "Gwendolyn",
            last_name: "Henderson",
        },
        fish_tag: "ABCDEFGHIJ",
        trip_code: "T8",
        st_jeans_num: "SJ-1008",
        ordered_at: "2026-03-08T22:45:00Z",
    },
]

function compareValues(a, b) {
    return String(a ?? "").localeCompare(String(b ?? ""), undefined, { sensitivity: "base" })
}

/**
 * @param {typeof ORDERS_PAGE_TEST_ORDERS} orders
 * @param {string} sortField
 * @param {"asc" | "desc"} sortDirection
 */
function sortTestOrders(orders, sortField, sortDirection) {
    const dir = sortDirection === "asc" ? 1 : -1
    const field = sortField || "ordered_at"

    return [...orders].sort((a, b) => {
        /** @type {string | undefined} */
        let left
        /** @type {string | undefined} */
        let right

        switch (field) {
            case "master":
                left = a.master
                right = b.master
                break
            case "status":
                left = a.status
                right = b.status
                break
            case "ship_first_name":
                left = a.ship_to?.first_name
                right = b.ship_to?.first_name
                break
            case "ship_last_name":
                left = a.ship_to?.last_name
                right = b.ship_to?.last_name
                break
            case "fish_tag":
                left = a.fish_tag
                right = b.fish_tag
                break
            case "trip_code":
                left = a.trip_code
                right = b.trip_code
                break
            case "st_jeans_num":
                left = a.st_jeans_num
                right = b.st_jeans_num
                break
            case "ordered_at":
            default:
                left = a.ordered_at
                right = b.ordered_at
                break
        }

        return compareValues(left, right) * dir
    })
}

/**
 * @param {string} searchText
 * @param {typeof ORDERS_PAGE_TEST_ORDERS} orders
 */
function filterTestOrdersBySearch(searchText, orders) {
    const query = String(searchText ?? "").trim().toLowerCase()
    if (!query) {
        return orders
    }

    return orders.filter((order) => {
        const haystack = [
            order.master,
            order.status,
            order.fish_tag,
            order.trip_code,
            order.st_jeans_num,
            order.ship_to?.first_name,
            order.ship_to?.last_name,
        ].join(" ").toLowerCase()
        return haystack.includes(query)
    })
}

/**
 * Simulates api.getOrders for the orders page test fixture set.
 *
 * @param {{
 *   page: number,
 *   perPage: number,
 *   sortField?: string,
 *   sortDirection?: "asc" | "desc",
 *   searchText?: string,
 * }} args
 * @returns {{ data: typeof ORDERS_PAGE_TEST_ORDERS, total: number }}
 */
export function mockOrdersPageGetOrders({
    page,
    perPage,
    sortField = "ordered_at",
    sortDirection = "desc",
    searchText = "",
}) {
    const safePage = Number.isInteger(page) && page > 0 ? page : 1
    const safePerPage = Number.isInteger(perPage) && perPage > 0 ? perPage : 25

    let list = filterTestOrdersBySearch(searchText, ORDERS_PAGE_TEST_ORDERS)
    list = sortTestOrders(list, sortField, sortDirection)

    const total = list.length
    const start = (safePage - 1) * safePerPage
    const data = list.slice(start, start + safePerPage)

    return { data, total }
}
